/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  FiArrowLeft,
  FiChevronRight,
  FiEdit2,
  FiArchive,
  FiDollarSign,
  FiTrendingUp,
  FiUsers,
  FiCheckSquare,
  FiFlag,
  FiPieChart,
  FiTag,
  FiShield,
  FiCheck,
  FiBox,
  FiPlay,
  FiActivity,
} from 'react-icons/fi';

export type TabType =
  | 'Overview'
  | 'Ticket Sales'
  | 'Roles'
  | 'Tasks'
  | 'Budget'
  | 'Inventory'
  | 'Run of Show'
  | 'Activity';

interface ProjectHeaderBannerProps {
  project: any;
  handleUnarchive: () => void;
  canSeeBudget: boolean;
  projectCrew: any[];
  openTasks: any[];
  inProgressTasksCount: number;
  projectMilestones: any[];
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
}

export const ProjectHeaderBanner: React.FC<ProjectHeaderBannerProps> = ({
  project,
  handleUnarchive,
  canSeeBudget,
  projectCrew,
  openTasks,
  inProgressTasksCount,
  projectMilestones,
  activeTab,
  setActiveTab,
}) => {
  const getTabIcon = (tab: TabType, isActive: boolean) => {
    const iconClass = `w-4 h-4 transition-colors duration-200 ${
      isActive ? 'text-blue-400 font-bold' : 'text-zinc-550 group-hover:text-zinc-300'
    }`;

    switch (tab) {
      case 'Overview':
        return <FiPieChart className={iconClass} />;
      case 'Ticket Sales':
        return <FiTag className={iconClass} />;
      case 'Roles':
        return <FiShield className={iconClass} />;
      case 'Tasks':
        return <FiCheck className={iconClass} />;
      case 'Budget':
        return <FiDollarSign className={iconClass} />;
      case 'Inventory':
        return <FiBox className={iconClass} />;
      case 'Run of Show':
        return <FiPlay className={iconClass} />;
      case 'Activity':
        return <FiActivity className={iconClass} />;
    }
  };

  return (
    <>
      {/* Top Header / Breadcrumb */}
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 text-xs text-zinc-500 font-medium">
          <Link
            to="/projects"
            className="flex items-center gap-1.5 hover:text-white transition-colors cursor-pointer"
          >
            <FiArrowLeft className="w-3.5 h-3.5" />
            <span>Projects</span>
          </Link>
          <span className="text-zinc-600">/</span>
          <span className="text-white truncate max-w-64">{project.title}</span>
        </div>

        <div className="flex items-center gap-3">
          {project.linkedEvent && (
            <Link
              to="/events/$eventId"
              params={{ eventId: project.linkedEventUuid || '' }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold hover:bg-blue-500/20 transition-all cursor-pointer"
            >
              <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              <span>{project.linkedEvent}</span>
              <FiChevronRight className="w-3 h-3 text-blue-400/60" />
            </Link>
          )}
          {(!project.permissions || project.permissions.can_edit) && (
            <Link
              to="/projects/$projectId/edit"
              params={{ projectId: project.id }}
              className="flex items-center gap-2 px-5 py-2 rounded-full bg-white text-black text-xs font-bold hover:bg-zinc-100 transition-all cursor-pointer"
            >
              <FiEdit2 className="w-3.5 h-3.5" />
              <span>Edit Project</span>
            </Link>
          )}
        </div>
      </div>

      {/* Project Header Overview */}
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
              project.status === 'Active'
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                : project.status === 'Planning'
                  ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
            }`}
          >
            <span
              className={`w-1 h-1 rounded-full ${
                project.status === 'Active'
                  ? 'bg-emerald-400'
                  : project.status === 'Planning'
                    ? 'bg-amber-400'
                    : 'bg-blue-400'
              }`}
            />
            {project.status}
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mt-2">
          {project.title}
        </h1>
        <p className="text-zinc-500 text-xs mt-1.5 font-medium">
          {project.subtitle} • {project.startDate} — {project.endDate}
        </p>

        {project.isArchived && (
          <div className="mt-4 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-amber-400 text-xs font-medium">
            <div className="flex items-center gap-2">
              <FiArchive className="w-4 h-4 shrink-0" />
              <span>This project is archived and read-only. Unarchive it to make changes.</span>
            </div>
            <button
              type="button"
              onClick={handleUnarchive}
              className="px-4 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold transition-all cursor-pointer shrink-0 disabled:opacity-50"
            >
              Unarchive Project
            </button>
          </div>
        )}
      </div>

      {/* Metrics Grid Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {/* Budget metrics commented out as Budget Tracker is coming soon
        {canSeeBudget && (
          <div className="bg-[#0F0F0F] border border-white/5 rounded-2xl p-4 flex items-center justify-between">
            <div>
              <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">Budget</p>
              <p className="text-white text-xl font-bold mt-1">
                ${project.allocatedBudget.toLocaleString()}
              </p>
              <p className="text-zinc-500 text-[10px] font-medium mt-0.5">Allocated</p>
            </div>
            <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-400">
              <FiDollarSign className="w-5 h-5" />
            </div>
          </div>
        )}
        */}

        {/* Expenses metrics commented out as Budget Tracker is coming soon
        {canSeeBudget && (
          <div className="bg-[#0F0F0F] border border-white/5 rounded-2xl p-4 flex items-center justify-between">
            <div>
              <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">
                Expenses
              </p>
              <p className="text-white text-xl font-bold mt-1">
                ${project.spentBudget.toLocaleString()}
              </p>
              <p className="text-zinc-500 text-[10px] font-medium mt-0.5">Spent so far</p>
            </div>
            <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-400">
              <FiTrendingUp className="w-5 h-5" />
            </div>
          </div>
        )}
        */}

        <div className="bg-[#0F0F0F] border border-white/5 rounded-2xl p-4 flex items-center justify-between">
          <div>
            <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">Crew</p>
            <p className="text-white text-xl font-bold mt-1">
              {projectCrew.filter((cm) => cm.status === 'Active').length}
            </p>
            <p className="text-zinc-500 text-[10px] font-medium mt-0.5">Members</p>
          </div>
          <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-400">
            <FiUsers className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-[#0F0F0F] border border-white/5 rounded-2xl p-4 flex items-center justify-between">
          <div>
            <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">
              Open Tasks
            </p>
            <p className="text-white text-xl font-bold mt-1">{openTasks.length}</p>
            <p className="text-zinc-500 text-[10px] font-medium mt-0.5">
              {inProgressTasksCount} in progress
            </p>
          </div>
          <div className="p-2.5 rounded-xl bg-rose-500/10 text-rose-455 animate-pulse">
            <FiCheckSquare className="w-5 h-5" />
          </div>
        </div>

        <div className="bg-[#0F0F0F] border border-white/5 rounded-2xl p-4 flex items-center justify-between col-span-2 sm:col-span-1">
          <div>
            <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">
              Milestones
            </p>
            <p className="text-white text-xl font-bold mt-1">{projectMilestones.length}</p>
            <p className="text-zinc-500 text-[10px] font-medium mt-0.5">Upcoming</p>
          </div>
          <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400">
            <FiFlag className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Tab Selection */}
      <div className="flex items-center gap-1 border-b border-white/5 pb-0.5 mb-8 overflow-x-auto no-scrollbar">
        {(
          [
            'Overview',
            'Ticket Sales',
            'Roles',
            'Tasks',
            'Inventory',
            'Run of Show',
            'Activity',
            'Budget',
          ] as TabType[]
        ).map((tab) => {
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`group relative px-4 py-3 text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors duration-200 cursor-pointer flex items-center gap-2 ${
                isActive ? 'text-white font-bold' : 'text-zinc-550 hover:text-zinc-300'
              }`}
            >
              {getTabIcon(tab, isActive)}
              <span>{tab}</span>
              {isActive && (
                <motion.div
                  layoutId="activeSubtab"
                  className="absolute bottom-0 left-0 right-0 h-[2px] bg-blue-500 rounded-full"
                />
              )}
            </button>
          );
        })}
      </div>
    </>
  );
};
